import React, { FC, ReactNode } from "react";
import Customiser from "./Customiser";
import Gutter from "ui-kit/Gutter";
import { Typography } from "ui-kit/Typography";
import { useWebTranslation } from "translations";
import { formatCurrency, NON_BREAKING_SPACE } from "utils/utils";
import { Icon, Modal } from "ui-kit";
import Colors from "ui-kit/colors";

const InfoText = ({
  title,
  content,
}: {
  title: string;
  content?: ReactNode;
}) => {
  const { t } = useWebTranslation();
  const [showDialog, setShowDialog] = React.useState(false);
  const open = () => setShowDialog(true);
  const close = () => setShowDialog(false);

  return (
    <>
      <button
        onClick={open}
        style={{ all: "initial", cursor: "pointer" }}
        aria-label={t("global.help")}
      >
        <Icon
          name="question-circle"
          size="M"
          color={Colors.grey400}
          ariaHidden
        />
      </button>

      <Modal isOpen={showDialog} onClose={close} title={title}>
        {content}
      </Modal>
    </>
  );
};
type ChooseDeductibleProps = {
  currentTotal: number;
  currentDeductible: number;
  currentCopay: number;
  deductibles: { value: number; total: number }[];
  copays: { value: number; total: number }[];
  onChangeDeductibleCopay: (deductible: number, copay: number) => void;
  deductibleDescription?: string;
  copayDescription?: string;
  inverseCopayDisplay?: boolean;
};

const ChooseDeductible: FC<ChooseDeductibleProps> = ({
  currentTotal,
  currentCopay,
  currentDeductible,
  deductibles,
  copays,
  onChangeDeductibleCopay,
  deductibleDescription,
  copayDescription,
  inverseCopayDisplay,
}) => {
  const { t } = useWebTranslation();
  const isMultiDeductible = !!(deductibles.length && copays.length);

  return (
    <>
      {isMultiDeductible && (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Typography variant={"h3"} color="grey500">
              {t("global.deductible")}
            </Typography>
            <InfoText
              title={t("global.deductible")}
              content={
                <Typography style={{ whiteSpace: "pre-wrap" }}>
                  {t("quote_preview.deductible_description")}
                </Typography>
              }
            />
          </div>
          <Gutter size={3} />
          <Typography>{t("quote_preview.deductible")}</Typography>
        </>
      )}
      {deductibles.length > 0 && (
        <>
          <Gutter size={2} />
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Typography
              variant={isMultiDeductible ? "h4" : "h3"}
              color="grey500"
            >
              {t("global.fixed_deductible")}
            </Typography>
            <InfoText
              title={t("global.fixed_deductible")}
              content={
                <Typography style={{ whiteSpace: "pre-wrap" }}>
                  {t("global.fixed_deductible_description")}
                </Typography>
              }
            />
          </div>
          {deductibleDescription && (
            <>
              <Gutter size={1} />
              <Typography variant="body3">{deductibleDescription}</Typography>
            </>
          )}
          <Gutter size={3} />
          <Customiser
            name="deductible"
            options={deductibles.map(({ value, total }) => ({
              value,
              title: formatCurrency(value / 100),
              totalDiff: total - currentTotal,
            }))}
            value={currentDeductible}
            onChange={(deductible) =>
              onChangeDeductibleCopay(deductible, currentCopay)
            }
            direction="column"
          />
        </>
      )}
      {copays.length > 0 && (
        <>
          <Gutter size={2} />
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Typography
              variant={isMultiDeductible ? "h4" : "h3"}
              color="grey500"
            >
              {t("global.copay")}
            </Typography>

            <InfoText
              title={t("global.copay")}
              content={
                <Typography style={{ whiteSpace: "pre-wrap" }}>
                  {t("global.copay_description")}
                </Typography>
              }
            />
          </div>
          {copayDescription && (
            <>
              <Gutter size={1} />
              <Typography variant="body3">{copayDescription}</Typography>
            </>
          )}
          <Gutter size={3} />
          <Customiser
            name="copay"
            options={copays.map(({ value, total }) => {
              return {
                value,
                title: `${
                  (inverseCopayDisplay ? 1 - value : value) * 100
                }${NON_BREAKING_SPACE}%`,
                totalDiff: total - currentTotal,
              };
            })}
            value={currentCopay}
            onChange={(copay) =>
              onChangeDeductibleCopay(currentDeductible, copay)
            }
          />
        </>
      )}
    </>
  );
};

export default ChooseDeductible;
